/**
 * ProductDetail page styles
 * Clean, professional design
 *
 * Colour, radius and spacing come from `src/theme/tokens.js` (Phase Sweep
 * batch 4). Alpha tints keep their original weight and only move hue, so a
 * shadow or wash reads exactly as heavy as it did before.
 */

import { alpha } from '@mui/material/styles';
import { color, radius, r } from '../../theme/tokens';

export const styles = {
  // Page wrapper - clean white
  pageWrapper: {
    background: color.paper,
    minHeight: '100vh',
    pb: 5,
  },

  // Hero card
  heroCard: {
    borderRadius: radius.lg,
    overflow: 'hidden',
    backgroundColor: color.paper,
    boxShadow: `0 ${r(2)} ${r(8)} rgba(0, 0, 0, 0.06)`,
    border: `${r(1)} solid ${color.lineSoft}`,
  },

  // Image container - WHITE background
  imageContainer: {
    backgroundColor: color.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: { xs: 300, md: 380 },
    padding: { xs: 3, md: 4 },
    borderRight: { md: `${r(1)} solid ${color.lineSoft}` },
  },

  productImage: {
    maxWidth: '85%',
    maxHeight: { xs: 250, md: 320 },
    objectFit: 'contain',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },

  // Product info section - GREY background
  productInfo: {
    p: { xs: 3, md: 4 },
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    backgroundColor: color.surface,
  },

  // Brand
  brand: {
    color: color.inkSoft,
    fontWeight: 600,
    letterSpacing: '0.08em',
    fontSize: '0.75rem',
    mb: 1,
    textTransform: 'uppercase',
  },

  // Product title
  productTitle: {
    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    mb: 3,
    color: color.ink,
  },

  // Buy button — the affiliate CTA. Deep evergreen with white text, the one
  // sanctioned exception to the brief's one-action-colour rule; the Signal
  // Accent still owns every other action in the product. Ruled by Tanu on
  // 2026-08-11, STACK-510, and recorded in docs/design-language/DESIGN_TOKENS.md.
  // A function so it can reach `primary.contrastText` for the white rather
  // than writing a hex literal into this file.
  buyButton: (theme) => ({
    backgroundColor: color.evergreen,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: '0.95rem',
    padding: `${r(12)} ${r(28)}`,
    borderRadius: radius.md,
    textTransform: 'none',
    boxShadow: 'none',
    width: 'fit-content',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: color.evergreenHover,
      boxShadow: `0 ${r(4)} ${r(12)} ${alpha(color.evergreen, 0.25)}`,
    },
  }),

  // Section container
  section: {
    mt: 3,
    p: { xs: 2.5, md: 3 },
    borderRadius: radius.lg,
    backgroundColor: color.surface,
    border: `${r(1)} solid ${color.lineSoft}`,
  },

  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.05em',
    mb: 2,
    color: color.ink,
    textTransform: 'uppercase',
  },

  // Specification table
  specTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },

  specRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1.25,
    borderBottom: `${r(1)} solid ${color.lineSoft}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },

  specLabel: {
    fontSize: '0.875rem',
    color: color.inkSoft,
    fontWeight: 500,
  },

  specValue: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: color.ink,
    textAlign: 'right',
  },

  // Quality badge
  qualityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    padding: `${r(8)} ${r(14)}`,
    backgroundColor: color.paper,
    borderRadius: radius.sm,
    border: `${r(1)} solid ${color.line}`,
    mr: 1,
    mb: 1,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: color.accent,
      backgroundColor: color.mintSoft,
    },
  },

  qualityIcon: {
    fontSize: '1rem',
    color: color.accent,
  },

  qualityText: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: color.ink,
  },

  // Dietary chip
  dietaryChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    padding: `${r(6)} ${r(12)}`,
    backgroundColor: color.mint,
    borderRadius: radius.sm,
    mr: 1,
    mb: 1,
  },

  dietaryIcon: {
    fontSize: '0.9rem',
    color: color.evergreen,
  },

  dietaryText: {
    fontSize: '0.8rem',
    fontWeight: 600,
    // Evergreen, not the accent: this is body-size text on the mint ground,
    // where the accent measures ~3.0:1. Evergreen holds ~8:1.
    color: color.evergreen,
  },

  // Related products section with separator
  relatedSection: {
    mt: 5,
    pt: 4,
    borderTop: `${r(1)} solid ${color.line}`,
  },

  relatedHeader: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: color.ink,
    mb: 2.5,
  },
};

export default styles;
